/**
 * File Upload Validation Utility
 * Validates file type, size, and content to prevent malicious uploads
 */

// Allowed MIME types for images
const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
];

// Allowed file extensions
const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

// Maximum file sizes (in bytes)
export const MAX_FILE_SIZES = {
  logo: 5 * 1024 * 1024, // 5MB
  playerPicture: 3 * 1024 * 1024, // 3MB
  default: 5 * 1024 * 1024, // 5MB
};

/**
 * Validate file MIME type
 * @param {File} file - File to validate
 * @returns {boolean} True if valid MIME type
 */
export const validateMimeType = (file) => {
  if (!file || !file.type) return false;
  return ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase());
};

/**
 * Validate file extension
 * @param {File} file - File to validate
 * @returns {boolean} True if valid extension
 */
export const validateFileExtension = (file) => {
  if (!file || !file.name) return false;
  const extension = '.' + file.name.split('.').pop().toLowerCase();
  return ALLOWED_EXTENSIONS.includes(extension);
};

/**
 * Validate file size
 * @param {File} file - File to validate
 * @param {string} type - File type (logo, playerPicture, default)
 * @returns {boolean} True if within size limit
 */
export const validateFileSize = (file, type = 'default') => {
  if (!file || !file.size) return false;
  const maxSize = MAX_FILE_SIZES[type] || MAX_FILE_SIZES.default;
  return file.size <= maxSize;
};

/**
 * Validate image file content by attempting to load it
 * @param {File} file - File to validate
 * @returns {Promise<boolean>} True if valid image
 */
export const validateImageContent = (file) => {
  return new Promise((resolve) => {
    if (!file) {
      resolve(false);
      return;
    }

    // Check MIME type first
    if (!validateMimeType(file)) {
      resolve(false);
      return;
    }

    // Try to load as image to verify it's actually an image
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      // Additional check: ensure it has valid dimensions
      if (img.width > 0 && img.height > 0) {
        resolve(true);
      } else {
        resolve(false);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };

    img.src = url;
  });
};

/**
 * Comprehensive file validation
 * @param {File} file - File to validate
 * @param {object} options - Validation options
 * @returns {Promise<object>} { isValid: boolean, errors: string[] }
 */
export const validateFile = async (file, options = {}) => {
  const errors = [];
  const { type = 'default', requireImageContent = true, skipSizeCheck = false } = options;

  if (!file) {
    return { isValid: false, errors: ['No file provided'] };
  }

  // Check file size
  if (!skipSizeCheck) {
    if (!validateFileSize(file, type)) {
      const maxSizeMB = (MAX_FILE_SIZES[type] || MAX_FILE_SIZES.default) / (1024 * 1024);
      errors.push(`File size must be less than ${maxSizeMB}MB`);
    }
  }

  // Check file extension
  if (!validateFileExtension(file)) {
    errors.push(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
  }

  // Check MIME type
  if (!validateMimeType(file)) {
    errors.push(`Invalid file type. Allowed MIME types: ${ALLOWED_IMAGE_TYPES.join(', ')}`);
  }

  // Validate image content (if required)
  if (requireImageContent && errors.length === 0) {
    const isValidContent = await validateImageContent(file);
    if (!isValidContent) {
      errors.push('File is not a valid image or is corrupted');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get file type from MIME type
 * @param {string} mimeType - MIME type
 * @returns {string} File extension or 'unknown'
 */
export const getFileExtension = (mimeType) => {
  const mimeMap = {
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'image/gif': '.gif',
    'image/webp': '.webp',
  };
  return mimeMap[mimeType?.toLowerCase()] || 'unknown';
};

